import styled, { css } from "styled-components";

import Grid from "@material-ui/core/Grid";

import colors, { transparentBg } from "core/colors";
import { regularFamily } from "core/fontStyles";
import { easingSeconds } from "core/animation";
import breakpoints from "core/breakpoints";
import { PopoverTriggerButton, ReakitPopover } from "core/Popover/styles";
import BodyText from "core/BodyText";

import Palette from "core/ReusableIcons/Palette";

import { normalizeHexCode } from "icons/IconEditor/helpers";
import { convertPxToRem } from "frontend/components/helpers";
import { DEFAULT_CONTAINER_COLOR } from "icons/IconEditor/constants";
import { REUSABLE_BOX_STYLES } from "core/Tooltip/styles";
import Flexbox from "core/Flexbox";

// reusable constants and helpers:
const SWATCH_TRIGGER_HOVER_STYLE = css`
    margin-left: ${convertPxToRem({ px: -1 })};
    margin-top: ${convertPxToRem({ px: -1 })};
    border: ${convertPxToRem({ px: 1 })} solid transparent;
    border-radius: 0;

    &:hover,
    &:active {
        border-color: ${colors.primary.diamond};
        box-shadow: 0px 0px 0px ${convertPxToRem({ px: 2 })}
            ${colors.functional.secondary.parrot};
    }
`;

const INTERIOR_SWATCH_TRIGGER_HOVER_STYLE = css`
    border: ${convertPxToRem({ px: 1 })} solid transparent;
    border-radius: 0;

    &:hover,
    &:active {
        border-color: ${colors.functional.secondary.parrot};
        box-shadow: inset 0 0 0 ${convertPxToRem({ px: 1 })}
            ${colors.primary.diamond};
    }

    &:hover:disabled {
        cursor: default;
    }
`;

const colorPickerButton = css`
    border-radius: 0;
    border: none;
    cursor: pointer;
`;

const getSwatchBorder = ({ color, containerBgColor }) => {
    const normalizedSwatchColor = normalizeHexCode({ hexCode: color });
    const normalizedContainerBgColor = normalizeHexCode({
        hexCode: containerBgColor
    });

    const borderColor =
        normalizedSwatchColor === normalizedContainerBgColor
            ? colors.primary.scheifer
            : "transparent";

    return css`
        border: 1px solid ${borderColor};
    `;
};

const getSwatchBGColor = color => {
    color = normalizeHexCode({ hexCode: color });

    if (!color) {
        return "";
    } else {
        return color
            ? color
            : normalizeHexCode({ hexCode: colors.secondary.onyx });
    }
};

const setBgColor = ({ color }) => ({
    style: {
        backgroundColor: getSwatchBGColor(color)
    }
});

const swatchTransparentBg = css`
    ${({ color, mainSwatch }) => {
        color = normalizeHexCode({ hexCode: color });

        return (
            !color &&
            css`
                background-size: ${mainSwatch ? "10px" : "11px"};
                ${transparentBg}
            `
        );
    }}
`;

// styled components:
export const PopoverTrigger = styled.div.attrs(setBgColor)`
    ${colorPickerButton}
    ${swatchTransparentBg}
    width: ${({ size }) => size};
    height: ${({ size }) => size};
    ${({ color }) =>
        getSwatchBorder({ color, containerBgColor: DEFAULT_CONTAINER_COLOR })}
`;

export const PopoverContent = styled.div`
    .react-colorful {
        width: 100%;
        height: ${convertPxToRem({ px: 124 })};
        margin: auto;
        margin-top: ${convertPxToRem({ px: 16 })};
    }

    .react-colorful__hue {
        margin-top: ${convertPxToRem({ px: 10 })};
        height: ${convertPxToRem({ px: 10 })};
        border-radius: ${convertPxToRem({ px: 10 })};
    }

    .react-colorful__hue-pointer {
        width: ${convertPxToRem({ px: 6 })};
        height: ${convertPxToRem({ px: 6 })};

        .react-colorful__pointer-fill {
            // !important overrides dynamic styles added inline to the element via js:
            background-color: ${colors.primary.snow} !important;
        }
    }

    .react-colorful__saturation {
        border-radius: ${convertPxToRem({ px: 2 })};
        border-bottom: 0;
    }

    .react-colorful__saturation-pointer {
        width: ${convertPxToRem({ px: 14 })};
        height: ${convertPxToRem({ px: 14 })};

        .react-colorful__pointer-fill {
            border: ${convertPxToRem({ px: 1 })} solid
                ${colors.primary.scheifer};
        }
    }
`;

export const SelectedColor = styled.div.attrs(setBgColor)`
    display: inline-block;
    width: ${convertPxToRem({ px: 24 })};
    height: ${convertPxToRem({ px: 24 })};
    margin-right: ${convertPxToRem({ px: 8 })};
    border-radius: 50%;
    ${swatchTransparentBg}
    ${({ color }) =>
        getSwatchBorder({
            color,
            containerBgColor: REUSABLE_BOX_STYLES.BOX_BACKGROUND({
                darkMode: true
            })
        })}
`;

export const StyledColorInput = styled.input.attrs({
    type: "text"
})`
    width: ${({ width }) => width};
    height: ${convertPxToRem({ px: 28 })};
    padding: ${convertPxToRem({ px: 3 })} ${convertPxToRem({ px: 6 })};
    border-radius: ${convertPxToRem({ px: 3 })};
    border: 1px solid ${colors.primary.scheifer};
    color: ${colors.secondary.orca};
    font: 300 ${convertPxToRem({ px: 14 })} ${regularFamily};
    line-height: ${convertPxToRem({ px: 16 })};
`;

export const ColorInputContainer = styled.div`
    flex: 1 1 auto;
    text-align: left;
`;

export const ColorSelectionContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const PaletteButtonContainer = styled.div``;

export const PickerIconButton = styled.button`
    cursor: pointer;
    border: 0;
    padding: 0;
    margin: 0;
    background: none;
    border-radius: 50%;
    width: ${convertPxToRem({ px: 16 })};
    height: ${convertPxToRem({ px: 16 })};

    img {
        width: 100%;
    }

    &,
    img,
    svg,
    svg path {
        transition: all ${easingSeconds};
    }

    &:hover {
        img {
            filter: grayscale();
        }

        ${Palette} {
            path {
                fill: ${colors.secondary.orca};
            }
        }
    }

    /* IE10 and IE11 support - filter: does not work in these browsers */
    @media ${breakpoints.browserSupport.ie10And11} {
        &:hover {
            box-shadow: 0 0 0 3px ${colors.secondary.oolong};

            img,
            ${Palette} {
                opacity: 0.6;
            }
        }
    }
`;

export const TitleContainer = styled(Flexbox).attrs({
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
})`
    margin-bottom: ${convertPxToRem({ px: 8 })};
`;

// RecentColors
export const SwatchBodyText = styled(BodyText)`
    margin-bottom: ${convertPxToRem({ px: 4 })};
`;

export const Swatch = styled.button.attrs(setBgColor)`
    ${colorPickerButton}
    position: relative;
    width: ${convertPxToRem({ px: 22 })};
    height: ${convertPxToRem({ px: 22 })};
    display: flex;
    align-items: center;
    justify-content: center;
    ${({ color }) =>
        getSwatchBorder({
            color,
            containerBgColor: REUSABLE_BOX_STYLES.BOX_BACKGROUND({
                darkMode: true
            })
        })}

    ${swatchTransparentBg}

    // only show focus on keyboard not with mouse:
    &:focus:not(.focus-visible) {
        outline: none;
        box-shadow: none;
        border: none;
    }

    &:focus {
        box-shadow: inset 0 0 0 3px ${colors.functional.secondary.parrot};
        outline: none;
        border: none;
    }

    ${INTERIOR_SWATCH_TRIGGER_HOVER_STYLE}
`;

export const ContainerGrid = styled(Grid)`
    margin-top: ${convertPxToRem({ px: 16 })};
`;

export const TooltipContainer = styled.div`
    line-height: 0; // removes extra 4px at bottom of container
    ${PopoverTriggerButton} {
        ${SWATCH_TRIGGER_HOVER_STYLE}

        // only show focus on keyboard not with mouse:
        &:focus:not(.focus-visible) {
            outline: none;
            box-shadow: none;
            border: ${convertPxToRem({ px: 1 })} solid transparent;
        }
    }

    ${ReakitPopover} {
        height: ${convertPxToRem({ px: 222 })};
    }
`;
